<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="ac-question">
        <span class="ac-bar ac-bar--xl"></span>
      </div>
      <div class="ac-player">
        <button class="ac-play" tabindex="-1">
          <span class="material-symbols-rounded">pause</span>
        </button>
        <div class="ac-wave">
          <span
            v-for="i in 26"
            :key="i"
            class="ac-bar-tick"
            :class="{ '--played': i <= 14 }"
            :style="{ '--tick-i': i, '--h': heights[i % heights.length] + 'px' }"
          ></span>
        </div>
        <span class="ac-time">0:42</span>
      </div>
      <div class="ac-reactions">
        <div
          v-for="(r, i) in reactions"
          :key="r.emoji"
          class="ac-react"
          :style="{ '--react-i': i }"
        >
          <span class="ac-emoji">{{ r.emoji }}</span>
          <span class="ac-react-count">{{ r.count }}</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="ac-phone-q"></span>
      <div class="ac-phone-player">
        <button class="ac-phone-play" tabindex="-1">
          <span class="material-symbols-rounded">play_arrow</span>
        </button>
        <div class="ac-phone-wave">
          <span
            v-for="i in 14"
            :key="i"
            class="ac-bar-tick"
            :class="{ '--played': i <= 6 }"
            :style="{ '--tick-i': i, '--h': heights[i % heights.length] + 'px' }"
          ></span>
        </div>
      </div>
      <div class="ac-phone-reacts">
        <button
          v-for="(r, i) in ['🔥', '👏', '😂', '❤️']"
          :key="r"
          class="ac-phone-react"
          :class="{ '--tapped': i === 0 }"
          tabindex="-1"
        >
          {{ r }}
        </button>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewAudioClip',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      heights: [10, 18, 6, 22, 14, 8, 24, 12, 16, 9, 20, 7],
      reactions: [
        { emoji: '🔥', count: 24 },
        { emoji: '👏', count: 18 },
        { emoji: '😂', count: 12 },
        { emoji: '❤️', count: 9 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.ac-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.ac-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.ac-player {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 8px;
}
.ac-play {
  appearance: none; border: none;
  width: 32px; height: 32px;
  border-radius: 999px;
  background: var(--accent); color: white;
  display: grid; place-items: center;
  cursor: pointer;
}
.ac-play .material-symbols-rounded { font-size: 18px; }
.ac-wave {
  display: flex; align-items: center;
  gap: 2px;
  height: 28px;
}
.ac-bar-tick {
  display: block;
  width: 2px;
  height: var(--h, 12px);
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  animation: ac-bounce 1.2s ease-in-out infinite;
  animation-delay: calc(var(--tick-i, 0) * 50ms);
}
.ac-bar-tick.--played {
  background: var(--accent);
}
.ac-time {
  font-size: 10px; font-weight: 800;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

.ac-reactions {
  display: flex;
  gap: 6px;
}
.ac-react {
  display: flex; align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.04);
  border: 1px solid rgba(15, 15, 20, 0.06);
  animation: ac-react-pop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--react-i, 0) * 200ms + 800ms);
}
.ac-emoji { font-size: 12px; }
.ac-react-count {
  font-size: 10px; font-weight: 700;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

/* ---- Audience phone ---- */
.ac-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.ac-phone-player {
  display: grid;
  grid-template-columns: 26px 1fr;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 8px;
}
.ac-phone-play {
  appearance: none; border: none;
  width: 26px; height: 26px;
  border-radius: 999px;
  background: var(--accent); color: white;
  display: grid; place-items: center;
  cursor: pointer;
}
.ac-phone-play .material-symbols-rounded { font-size: 14px; }
.ac-phone-wave {
  display: flex; align-items: center;
  gap: 2px;
  height: 22px;
}
.ac-phone-reacts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}
.ac-phone-react {
  appearance: none;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.1);
  border-radius: 8px;
  padding: 6px 0;
  font-size: 16px;
  cursor: pointer;
}
.ac-phone-react.--tapped {
  background: color-mix(in srgb, var(--accent) 12%, white);
  border-color: var(--accent);
  animation: ac-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

@keyframes ac-bounce {
  0%, 100% { transform: scaleY(1); }
  50%      { transform: scaleY(0.4); }
}
@keyframes ac-react-pop {
  0%, 60%   { transform: scale(1); }
  72%       { transform: scale(1.18); }
  85%, 100% { transform: scale(1); }
}
@keyframes ac-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); transform: scale(1); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); transform: scale(1.15); }
  35%, 100%{ box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .ac-bar-tick, .ac-react, .ac-phone-react.--tapped { animation: none; }
}
</style>
